import { gql } from '~src/generated/portal/gql';

export const PartnerParts = gql(
  `fragment PartnerParts on Partner {
    id
    name
    partnerType
    workstreams
    languages
    confluenceName      
    primaryTAM {
      ... on UserProfile {
        id
        email
        firstName
        lastName
      }
    }
    backupTAM {
      ... on UserProfile {
        id
        email
        firstName
        lastName
      }
    }
    watchers {
      ... on UserWatcher {
        userProfile {
          email
          id
          firstName
          lastName
        }
      }
    }
  }
`,
);

export const VersionParts = gql(
  `fragment VersionParts on Version {
    major
    minor
    patch
    string
  }
`,
);

export const FirmwareVersionsParts = gql(
  `fragment FirmwareVersionsParts on FirmwareVersion {
    version
    dateAdded
  }
`,
);

export const DeviceRowParts = gql(
  `fragment DeviceRowParts on Device {
    id
    alias
    status
    underTest
    agentVersion {
      ...VersionParts
    }
    user {
      id
      profileName
      lastName
      firstName
    }
    testStatus {
      test {
        id
        cacheId
        name
      }
    }
  }
`,
);

export const ProjectParts = gql(
  `fragment ProjectParts on Project {
    id
    name
    totalFirmwareVersions
    rcVersion {
      version
      dateAdded
    }
    model {
      name
      id
    }
    partner {
      id
      name
      languages
      workstreams
      confluenceName
    }
  }
`,
);

export const ProjectListParts = gql(
  `fragment ProjectListParts on Project {
    name
    id
    referenceImplementation
    submissionStatus
    projectStatus
    featureTags
    firmwareVersions(offset: 0, limit: 1) {
      version
      dateAdded
    }
    comments {
      author
      userId
      comment
    }
    totalFirmwareVersions
    partner {
        name
        id
        workstreams
        languages
        confluenceName
    }
    currentSubmission {
      ...SubmissionParts
    }
    adkVersion {
      ...VersionParts
    }
    appVersion {
      ...VersionParts
    }
    rcVersion {
      version
      dateAdded
    }
    projectScope {
      product
    }
  }
`,
);

export const SubmissionParts = gql(
  `fragment SubmissionParts on Submission {
    id
    timestamp
    firmwareVersion
    status
    submissionFile {
      fileName
    }
    submissionInstructions {
      deliveryId
      file {
        fileName
      }
    }
    certificationReport {
      fileName
    }
    project {
      id
      name
      featureTags
      comments {
        author
        userId
        comment
      }
      firmwareVersions(offset: 0, limit: 1) {
        version
        dateAdded
      }
      partner {
        name
        id
        languages
        workstreams
        confluenceName
      }
      totalFirmwareVersions
      submissionStatus
    }
    userId
    partner {
      name
      languages
      workstreams
      confluenceName
      id
    }
  }
`,
);

export const TestDetailParts = gql(
  `fragment TestDetailParts on ADKTestWithStatus {
    name
    description
    id
    type
    category
    subcategory
    notes
    timestamp
    projectId
    stackImpact
    version {
      ...VersionParts
    }
    procedure {
      ... on ADKTestProcedureAuto {
        given
        when
        then
      }
      ... on ADKTestProcedureManual {
        richObjective {
          contentHtml
          contentPlain
        }
        richDescription {
          contentHtml
          contentPlain
        }
        richExpectedResult {
          contentHtml
          contentPlain
        }
      }
    }
    lastRunStatus {
      id
      result
      firmwareVersion
    }
    statusInRc {
      id
      result
      firmwareVersion
    }
  }
`,
);

export const TestCommentParts = gql(
  `fragment TestCommentParts on TestComment {
    id
    timestamp
    user {
      id
      profileName
      firstName
      lastName
    }
    richTextContent {
      contentPlain
      contentHtml
    }
    files {
      fileName
      path
    }
  }
`,
);

export const TestWithStatusParts = gql(
  `fragment TestWithStatusParts on ADKTestWithStatus {
    projectId
    id
    name
    description
    type
    subcategory
    preCertificationTestStatus {
      lastRunStatus {
        id
        result
        firmwareVersion
      }
      statusInRc {
        id
        result
        firmwareVersion
      }
    }
    certificationTestStatus {
      lastRunStatus {
        id
        result
        firmwareVersion
      }
      statusInRc {
        id
        result
        firmwareVersion
      }
    }
    timestamp
  }
`,
);

export const ModelAttributesParts = gql(
  `fragment ModelAttributesParts on ModelAttributes {
    platformCharacteristics {
      operator {
        value
        changeRequested {
          value
        }
      }
      country {
        value
        changeRequested {
          value
        }
      }
      vendor {
        value
        changeRequested {
          value
        }
      }
      number {
        value
        changeRequested {
          value
        }
      }
      name {
        value
        changeRequested {
          value
        }
      }
      deviceType {
        value
        changeRequested {
          value
        }
      }

      thirdPartyApps {
        deployed {
          value
          changeRequested {
            value
          }
        }
        deployedApps {
          value
          changeRequested {
            value
          }
        }
        deploymentMethod {
          value
          changeRequested {
            value
          }
        }
      }

      memory {
        totalMemory {
          value
          changeRequested {
            value
          }
        }
        deviceMemoryType {
          value
          changeRequested {
            value
          }
        }
        totalPersistentStorage {
          value
          changeRequested {
            value
          }
        }
      }
      storage {
        storageType {
          value
          changeRequested {
            value
          }
        }
        totalStorage {
          value
          changeRequested {
            value
          }
        }
        storageFileSystem {
          value
          changeRequested {
            value
          }
        }
      }
      operatingSystem {
        operatingSystem {
          value
          changeRequested {
            value
          }
        }
        operatingSystemVersion {
          value
          changeRequested {
            value
          }
        }
      }
      systemSoftware {
        systemSoftware {
          value
          changeRequested {
            value
          }
        }
        systemSoftwareVersion {
          value
          changeRequested {
            value
          }
        }
      }
    }
    soc {
      vendor {
        value
        changeRequested {
          value
        }
      }
      model {
        value
        changeRequested {
          value
        }
      }
      boxMode {
        value
        changeRequested {
          value
        }
      }
      boxModeValue {
        value
        changeRequested {
          value
        }
      }
      architecture {
        value
        changeRequested {
          value
        }
      }
      chipsetRevision {
        value
        changeRequested {
          value
        }
      }
      supportContract {
        value
        changeRequested {
          value
        }
      }
      numberOfCores {
        value
        changeRequested {
          value
        }
      }
      software {
        value
        changeRequested {
          value
        }
      }
    }
  }
`,
);

export const SystemAndEnvironmentParts = gql(
  `fragment SystemAndEnvironmentParts on ModelSystemAndEnvironment {
    systemAndEnvironment {
      toolchain {
        value
        changeRequested {
          value
        }
      }
      toolchainVersion {
        value
        changeRequested {
          value
        }
      }
      toolchainPrefix {
        value
        changeRequested {
          value
        }
      }
      toolchainLink {
        value
        changeRequested {
          value
        }
      }
      toolchainFile {
        value {
          fileName
          path
        }
        changeRequested {
          value
        }
      }
      toolchainModifications {
        value
        changeRequested {
          value
        }
      }
      gpu {
        value
        changeRequested {
          value
        }
      }
      gpuApiSupport {
        value
        changeRequested {
          value
        }
      }
      softwareStack {
        value
        changeRequested {
          value
        }
      }
      cpuBenchmark {
        value
        changeRequested {
          value
        }
      }
      vpn {
        value
        changeRequested {
          value
        }
      }
      vpnDetails {
        value
        changeRequested {
          value
        }
      }
      broadcastStreamer {
        value
        changeRequested {
          value
        }
      }
      appStateManagement {
        value
        changeRequested {
          value
        }
      }
    }
    resourceAvailability {
      memoryForADKBinary {
        rom {
          value
          changeRequested {
            value
          }
        }
        appPackageSizeLimit {
          value
          changeRequested {
            value
          }
        }
      }
      memoryForApp {
        foregroundAvailableMemory {
          value
          changeRequested {
            value
          }
        }
        backgroundAvailableMemory {
          value
          changeRequested {
            value
          }
        }
      }
      writableStorage {
        persistentStorage {
          value
          changeRequested {
            value
          }
        }
        nonPersistentStorage {
          value
          changeRequested {
            value
          }
        }
      }
      secureStorage {
        persistentSecureStorage {
          value
          changeRequested {
            value
          }
        }
        writeRestrictions {
          value
          changeRequested {
            value
          }
        }
      }
    }
    systemAccessibility {
      edidExchangeAccessible {
        edidExchangeAccessible {
          value
          changeRequested {
            value
          }
        }
        edidExchangeNotAccessibleReason {
          value
          changeRequested {
            value
          }
        }
      }
      frameRateSettingsAccessible {
        frameRateSettingsAccessible {
          value
          changeRequested {
            value
          }
        }
        frameRateSettingsNotAccessibleReason {
          value
          changeRequested {
            value
          }
        }
      }
      rightToDistributeToolchain {
        value
        changeRequested {
          value
        }
      }
      authorizedToShareBuildSettings {
        value
        changeRequested {
          value
        }
      }
    }
  }
`,
);

export const SecurityParts = gql(
  `fragment SecurityParts on ModelSecurity {
    copyProtectionAndRightsEntitlements {
      drmProvider {
        value
        changeRequested {
          value
        }
      }
      wideVineVersionAndLevel {
        value
        changeRequested {
          value
        }
      }
      playReadyVersionAndLevel {
        value
        changeRequested {
          value
        }
      }
      keySlotsAvailable {
        value
        changeRequested {
          value
        }
      }
      analogCopyProtection {
        value
        changeRequested {
          value
        }
      }
      hdcp {
        value
        changeRequested {
          value
        }
      }
    }
    securityEnvironmentAndPolicy {
      usesSecureFirmwareDownload {
        value
        changeRequested {
          value
        }
      }
      usesSignedFirmwareSecureBoot {
        value
        changeRequested {
          value
        }
      }
      usesHardwareRootOfTrust {
        value
        changeRequested {
          value
        }
      }
      usesTamperResistantCode {
        value
        changeRequested {
          value
        }
      }
      usesTrustedExecutionEnvironment {
        value
        changeRequested {
          value
        }
      }
      usesSecureVideoPath {
        value
        changeRequested {
          value
        }
      }
      usesRootedDeviceProtection {
        value
        changeRequested {
          value
        }
      }
      usesAppCodeSigning {
        value
        changeRequested {
          value
        }
      }
      usesInstallationRestrictions {
        value
        changeRequested {
          value
        }
      }
    }
  }
`,
);

export const PresentationParts = gql(
  `fragment PresentationParts on ModelPresentation {
    mediaPath {
      analogSupportFor480p {
        value
        changeRequested {
          value
        }
      }
      maxStreamingThroughput {
        value
        changeRequested {
          value
        }
      }
      frameRatesSupported {
        value
        changeRequested {
          value
        }
      }
      canControlRefreshRate {
        value
        changeRequested {
          value
        }
      }
      canMatchOutputFrameRate {
        value
        changeRequested {
          value
        }
      }
    }
    avInterface {
      hdmiVersion {
        value
        changeRequested {
          value
        }
      }
    }
    videoSupport {
      hevc {
        value
        changeRequested {
          value
        }
      }
      avc {
        value
        changeRequested {
          value
        }
      }
    }
    audioSupport {
      audioCodecsSupported {
        value
        changeRequested {
          value
        }
      }
      dolbyAtmos {
        value
        changeRequested {
          value
        }
      }
      dolbyMsSupport {
        value
        changeRequested {
          value
        }
      }
      dolbyCertified {
        value
        changeRequested {
          value
        }
      }
      onboardDecode {
        value
        changeRequested {
          value
        }
      }
      bitstreamOut {
        value
        changeRequested {
          value
        }
      }
      maxThroughput {
        value
        changeRequested {
          value
        }
      }
    }
    highDynamicRange {
      hdrType {
        value
        changeRequested {
          value
        }
      }
      vs10Version {
        value
        changeRequested {
          value
        }
      }
      dolbyCertified {
        value
        changeRequested {
          value
        }
      }
    }
    graphicsPlane {
      maxResolution {
        value
        changeRequested {
          value
        }
      }
      maxResolutionVideoPlayback {
        value
        changeRequested {
          value
        }
      }
      correctionForHdr {
        value
        changeRequested {
          value
        }
      }
    }
  }
`,
);

export const ModelDetailsParts = gql(
  `fragment ModelDetailsParts on ModelDetails {
    modelAttributes {
      ...ModelAttributesParts
    }
    systemAndEnvironment {
      ...SystemAndEnvironmentParts
    }
    security {
      ...SecurityParts
    }
    presentation {
      ...PresentationParts
    }
  }
`,
);
export const projectSubscriptionUpdate = gql(
  `fragment ProjectEvent on Project {
    id
    projectStatus
    submissionStatus

    rcVersion {
      __typename
      version
      dateAdded
    }

    progress {
      testsPassed
      totalTests
    }

    adkVersion {
      __typename
      major
      minor
      patch
    }

    appVersion {
      major
      minor
      patch
    }
    rcVersion {
      __typename
      version
      dateAdded
    }
  }
`,
);
export const testSubscriptionUpdate = gql(
  `fragment TestEvent on ADKTestWithStatus {
    name
    __typename
    id
    timestamp
    version {
      major
      minor
      patch
    }
    preCertificationTestStatus {
      lastRunStatus {
        result
        firmwareVersion
      }
      statusInRc {
        result
        firmwareVersion
      }
    }
    certificationTestStatus {
      lastRunStatus {
        result
        firmwareVersion
      }
      statusInRc {
        result
        firmwareVersion
      }
    }
    projectId
  }
`,
);

export const ADkTest = gql(
  `fragment ADKTestParts on ADKTest {
    id
    cacheId
    name
    type
    category
    subcategory
    featureTags
    timestamp
    version {
      major
      minor
      patch
      string
    }
    suiteTypeAndId {
      suiteType
      id
    }
    testScriptHistory {
      id
      suiteTypeAndId {
        suiteType
        id
      }
      testId
      fileName
      path
      timeStamp
    }
    defaultTestParameters {
      ... on ADKTestParameterBool {
        name
        valueBool
      }
      ... on ADKTestParameterString {
        name
        valueString
      }
      ... on ADKTestParameterNumber {
        name
        type
        valueNumber
        displayValue
      }
    }
  }`,
);

export const APItest = gql(
  `fragment APITestParts on APITest {
    id
    name
    displayId
    labels {
      label
    }
    procedure {
      steps {
        successCriteria
      }
    }
  }`,
);
export const TestExecution = gql(
  `fragment TestExecutionParts on TestExecution {
    id
    timestamp
    test {
      id
      cacheId
      name
      category
      subcategory
      type
      featureTags
      timestamp
      version {
        major
        minor
        patch
        string
      }
      suiteTypeAndId {
        suiteType
        id
      }
      testScriptHistory {
        id
        suiteTypeAndId {
          suiteType
          id
        }
        testId
        fileName
        path
        timeStamp
      }
    }
    result
    partner {
      ...PartnerParts
    }
    project {
      ...ProjectListParts
    }
    firmwareVersion
    startTime
    suiteTypeAndId {
      suiteType
      id
    }
  }`,
);

export const ADKTestWithStatus = gql(
  `fragment ADKTestWithStatus on ADKTestWithStatus {
    id
    name
    projectId
    type
    category
    subcategory
    featureTags
    version {
      ...VersionParts
    }
    timestamp
    preCertificationTestStatus {
      lastRunStatus {
        ...TestExecutionParts
      }
      statusInRc {
        ...TestExecutionParts
      }
    }
    certificationTestStatus {
      lastRunStatus {
        ...TestExecutionParts
      }
      statusInRc {
        ...TestExecutionParts
      }
    }
   }`,
);

export const TestStatusParts = gql(
  `fragment TestStatusParts on TestStatus {
    executionId
    executionType {
      ... on SingleTestExecution {
        testID
      }
      ... on TestSetExecution {
        testID
        testSet {
          id
          name
          type
          testIds
          tests(projectId: $projectId) {
            ... on ADKTest {
              ...ADKTestParts
            }
            ... on APITest {
              ...APITestParts
            }
          }
          testsWithStatus(projectId: $projectId) {
            ... on ADKTestWithStatus {
              ...ADKTestWithStatus
            }
          }
        }
      }
    }
  }
`,
);

export const deviceSubscriptionUpdate = gql(
  `fragment DeviceEvent on Device {
    __typename
    id
    underTest
    lastUsed
    status
    testStatus {
      __typename
      test {
        __typename
        id
        cacheId
        name
      }
    }
    user {
      __typename
      id
      profileName
      lastName
      firstName
    }
  }
`,
);
